import { useEffect, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import type { ReplaySummary, UserPublic } from '@ccc/shared-types';
import { CARD_CATALOG } from '@ccc/cards';
import { Panel } from '../components/Card';
import { Button } from '../components/Button';
import { apiJson } from '../lib/multiplayer';

interface RatingEntry {
  rating: number;
  rd: number;
  volatility: number;
  gamesPlayed: number;
}

interface ProfileResponse {
  user: UserPublic;
  ratings: Partial<Record<'bullet' | 'blitz' | 'rapid' | 'classical', RatingEntry>>;
  gameCount: number;
  recentGames: ReplaySummary[];
}

const TIME_CLASSES = [
  { id: 'bullet', label: 'Bullet' },
  { id: 'blitz', label: 'Blitz' },
  { id: 'rapid', label: 'Rapid' },
  { id: 'classical', label: 'Classical' },
] as const;

export function ProfileRoute() {
  const { username } = useParams({ from: '/profile/$username' });
  const [data, setData] = useState<ProfileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiJson<ProfileResponse>(`/api/users/${encodeURIComponent(username)}`)
      .then((d) => {
        if (alive) {
          setData(d);
          setError(null);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (alive) {
          setError(err.message);
          setData(null);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-full w-full max-w-[1100px] mx-auto px-5 md:px-10 py-6 pb-12 grid place-items-center">
        <p className="text-sm text-ink-muted">Loading profile…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-full w-full max-w-[1100px] mx-auto px-5 md:px-10 py-6 pb-12 grid place-items-center">
        <Panel className="p-6 text-center max-w-[440px]">
          <div className="font-display text-2xl font-bold">Profile not found</div>
          <p className="mt-2 text-sm text-ink-muted">
            {error ?? 'No player with that name.'}
          </p>
          <Link to="/" className="inline-block mt-4">
            <Button variant="secondary">Back home</Button>
          </Link>
        </Panel>
      </div>
    );
  }

  const { user, ratings, gameCount, recentGames } = data;
  const initials = displayInitials(user.displayName);
  const joined = new Date(user.createdAt);
  const joinedLabel = joined.toLocaleString(undefined, { year: 'numeric', month: 'long' });

  return (
    <div className="min-h-full w-full max-w-[1100px] mx-auto px-5 md:px-10 py-6 pb-12 flex flex-col gap-5">
      <Panel className="p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-4 sm:gap-6">
          <Avatar initials={initials} avatarUrl={user.avatarUrl} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-3xl sm:text-4xl font-bold truncate">
                {user.displayName}
              </h1>
              {user.isSupporter && <SupporterBadge />}
              {user.canAdmin && (
                <span className="px-2 py-0.5 rounded-md bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider">
                  {user.canAdmin && user.displayName === 'FM' ? 'Owner' : 'Admin'}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-ink-muted">
              @{user.username} · joined {joinedLabel} · {gameCount} public {gameCount === 1 ? 'game' : 'games'}
            </p>
          </div>
        </div>
      </Panel>

      <Panel className="p-5 sm:p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-3">
          Ratings
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TIME_CLASSES.map(({ id, label }) => {
            const r = ratings[id];
            return (
              <div
                key={id}
                className="rounded-md border border-ink/10 p-3 bg-surface-2"
              >
                <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
                {r ? (
                  <>
                    <div className="font-display text-2xl font-bold mt-1">
                      {Math.round(r.rating)}
                    </div>
                    <div className="text-xs text-ink-muted">
                      {r.gamesPlayed} {r.gamesPlayed === 1 ? 'game' : 'games'}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-display text-2xl font-bold mt-1 text-ink-muted">
                      —
                    </div>
                    <div className="text-xs text-ink-muted">Unrated</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel className="p-5 sm:p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-3">
          Recent games
        </h2>
        {recentGames.length === 0 ? (
          <p className="text-sm text-ink-muted">No finished public games yet.</p>
        ) : (
          <div className="grid gap-2">
            {recentGames.map((g) => (
              <Link
                key={g.id}
                to="/replays/$gameId"
                params={{ gameId: g.id }}
                className="rounded-md border border-ink/10 p-3 hover:bg-ink/[0.04] transition focus-ring"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold truncate">
                    {g.whiteName} vs {g.blackName}
                  </div>
                  <div className="text-xs text-ink-muted">
                    {g.likeCount} likes · {g.plyCount} plies
                  </div>
                </div>
                <div className="mt-1 text-xs text-ink-muted">
                  {gameSubtitle(g)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Avatar({ initials, avatarUrl }: { initials: string; avatarUrl?: string | undefined }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        width={80}
        height={80}
        className="h-20 w-20 rounded-full object-cover border border-ink/10 shrink-0"
      />
    );
  }
  return (
    <div className="h-20 w-20 rounded-full grid place-items-center bg-accent/10 text-accent font-display text-2xl font-bold border border-ink/10 shrink-0">
      {initials}
    </div>
  );
}

function SupporterBadge() {
  return (
    <span className="px-2 py-0.5 rounded-md bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider">
      Supporter
    </span>
  );
}

function displayInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

function gameSubtitle(g: ReplaySummary): string {
  const result =
    g.winner === 'draw'
      ? 'Drawn'
      : g.winner
      ? `${g.winner === 'white' ? g.whiteName : g.blackName} won`
      : 'Unfinished';
  const w = g.whiteCardId ? CARD_CATALOG.find((c) => c.id === g.whiteCardId)?.shortTitle : null;
  const b = g.blackCardId ? CARD_CATALOG.find((c) => c.id === g.blackCardId)?.shortTitle : null;
  const cards = [w, b].filter(Boolean).join(' vs ');
  return [g.timeClass, g.rated ? 'rated' : 'casual', result, cards].filter(Boolean).join(' · ');
}
